/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { SOUNDING_COLORS } from './drawUtils';
import type { SourceType } from '../../store/types';
import { getDisplayValue } from '../utils';

export const getLegendTitle = (source: SourceType): string => {
  const displayName = source.name || source.collectionId;
  const displayInstance = getDisplayValue(
    source.instanceId || '',
    "yyyy-MM-dd HH:mm'Z'",
  );
  return `${displayName} ${displayInstance}`;
};
interface SoundingsLegendProps {
  sourceSelections: SourceType[];
}

export const SoundingsLegend = ({
  sourceSelections,
}: SoundingsLegendProps): React.ReactElement => {
  return (
    <Stack
      direction="row"
      justifyContent="center"
      spacing={1}
      sx={{ flexWrap: 'wrap', paddingLeft: 2, paddingTop: 0.5 }}
      useFlexGap
    >
      {sourceSelections.map((source: SourceType, index) => {
        const title = getLegendTitle(source);
        return (
          <Tooltip
            key={`${source.collectionId}-${source.instanceId}`}
            title={title}
          >
            <Typography
              sx={{
                color: 'geowebColors.typographyAndIcons.text',
                fontSize: '10px',
              }}
              variant="caption"
            >
              <Box
                sx={{
                  width: '16px',
                  height: '8px',
                  backgroundColor: SOUNDING_COLORS[index],
                  display: 'inline-block',
                  marginRight: '4px',
                }}
              />
              {title}
            </Typography>
          </Tooltip>
        );
      })}
    </Stack>
  );
};
